import React, { useEffect } from 'react';
import {
  Paper,
  Button,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  MenuItem,
  Typography,
  Grid,
  InputAdornment,
} from '@mui/material';
import Form from 'mobx-react-form';
import { observer } from 'mobx-react-lite';

import { formatCurrencyFloat } from 'shared/utils/numberFormatters';
import NumericInput from 'components/shared/inputs/NumericInput';
import { OtherIncomeItem } from 'types/underwriting/income-approach';

import { VC_LOSS_OPTIONS, VCLossTypes } from '../constants';
import { otherIncomeFormHelpers } from './otherIncomeFormConfig';
import { reaction } from 'mobx';
import { useRootStore } from 'stores/RootStore';
import { get } from 'lodash';

const INCOME_ITEMS_TABLE_COLUMNS = ['#', 'Type', 'VC Loss %', 'Category', 'Annual Amount', 'Actions'];
const MAX_VC_LOSS_PERCENTAGE_VALUE = 100;

interface OtherIncomeProps {
  form: Form;
}

const OtherIncome: React.FC<OtherIncomeProps> = observer(({ form }) => {
  const { underwritingPageStore } = useRootStore();
  const { underwritingData } = underwritingPageStore;
  const residentialVCLossPercentage = get(underwritingData, 'incomeApproach.residential.vcLossPercentage', 0);
  const commercialVCLossPercentage = get(underwritingData, 'incomeApproach.commercial.vcLossPercentage', 0);

  const otherIncomeField = form.$('incomeApproach.otherIncome');
  const otherIncome = otherIncomeField.value;

  const totalAnnualOtherIncome = otherIncome.reduce(
    (acc: number, item: OtherIncomeItem) => acc + (item.amount || 0),
    0
  );

  useEffect(() => {
    const disposer = reaction(
      () => form.$('incomeApproach.otherIncome').value.map((item: OtherIncomeItem) => item.vcLossType),
      (vcLossTypes, prevVcLossTypes) => {
        if (vcLossTypes.length !== prevVcLossTypes?.length) {
          return;
        }
        vcLossTypes.forEach((type: VCLossTypes, idx: string | number) => {
          if (type !== prevVcLossTypes?.[idx]) {
            let percent = form.$(`incomeApproach.otherIncome.${idx}.vcLossPercentage`).value || 0;
            if (type === VCLossTypes.RESIDENTIAL) {
              percent = residentialVCLossPercentage;
            } else if (type === VCLossTypes.COMMERCIAL) {
              percent = commercialVCLossPercentage;
            }
            form.$(`incomeApproach.otherIncome.${idx}.vcLossPercentage`).set(percent);
          }
        });
      }
    );
    return () => disposer();
  }, [commercialVCLossPercentage, form, residentialVCLossPercentage]);

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6">Other Income</Typography>
      <Grid container spacing={2} sx={{ pt: 2, mb: 2, width: '100%' }} justifyContent="flex-end">
        <Grid item xs={3.5}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => otherIncomeFormHelpers.addNewOtherIncomeItem(form)}
          >
            Add Other Income Item
          </Button>
        </Grid>
        <Divider />
        <Grid item xs={12}>
          <Table sx={{ mb: 6 }} size="small">
            <TableHead
              sx={{
                '& .MuiTableCell-head': {
                  fontSize: 12,
                },
              }}
            >
              <TableRow>
                {INCOME_ITEMS_TABLE_COLUMNS.map((column) => (
                  <TableCell key={column} size="medium">
                    {column}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {otherIncome.map((item: OtherIncomeItem, index: number) => {
                const isResidentialIncomeType = item.vcLossType === VCLossTypes.RESIDENTIAL;
                const isCommercialIncomeType = item.vcLossType === VCLossTypes.COMMERCIAL;
                const vcLossPercentageDisabled = isResidentialIncomeType || isCommercialIncomeType;

                const vcLossTypeField = form.$(`incomeApproach.otherIncome.${index}.vcLossType`);
                const vcLossPercentageField = form.$(`incomeApproach.otherIncome.${index}.vcLossPercentage`);
                const nameField = form.$(`incomeApproach.otherIncome.${index}.name`);
                const amountField = form.$(`incomeApproach.otherIncome.${index}.amount`);

                return (
                  <TableRow key={`otherIncomeLineItems-${index}-row`} sx={{ height: 10 }}>
                    <TableCell sx={{ fontSize: 12 }}>{index + 1}</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>
                      <TextField {...vcLossTypeField.bind()} size="small" margin="normal" select fullWidth>
                        {VC_LOSS_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <NumericInput
                        {...vcLossPercentageField.bind()}
                        disabled={vcLossPercentageDisabled}
                        placeholder="VC Loss %"
                        size="small"
                        margin="normal"
                        allowNegative={false}
                        decimalScale={2}
                        max={MAX_VC_LOSS_PERCENTAGE_VALUE}
                        fullWidth
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        {...nameField.bind()}
                        size="small"
                        margin="normal"
                        fullWidth
                        placeholder="Other Income Category"
                      />
                    </TableCell>
                    <TableCell>
                      <NumericInput
                        {...amountField.bind()}
                        size="small"
                        margin="normal"
                        allowNegative={false}
                        decimalScale={2}
                        fullWidth
                        placeholder="Annual Amount"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => otherIncomeFormHelpers.removeOtherIncomeItem(form, index)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow key="total-row">
                <TableCell colSpan={3} />
                <TableCell>Total Annual Other Income:</TableCell>
                <TableCell key="totalAnnualOtherIncome-cell" colSpan={2} size="medium">
                  {formatCurrencyFloat(totalAnnualOtherIncome)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Grid>
      </Grid>
    </Paper>
  );
});

export { OtherIncome };
